import React, {useEffect, useState} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import type {VideoWrapperSizeModifier} from '@pexip/components';
import {VideoWrapper, Video} from '@pexip/components';

import {TestId} from '../../../test/testIds';

import {isVideoActive} from './SelfView.utils';
import {SelfviewOverlay} from './SelfviewOverlay.view';

import styles from './Selfview.module.scss';

export const Selfview: React.FC<
    React.PropsWithChildren<
        React.ComponentProps<typeof VideoWrapper> &
            Pick<
                React.ComponentProps<typeof SelfviewOverlay>,
                'labelContent' | 'labelTestId'
            > & {
                'data-testid'?: string;
                'wrapper-data-testid'?: string;
                isMirrored?: boolean;
                isMobileDevice?: boolean;
                isVideoInputMuted: boolean;
                localMediaStream?: MediaStream;
                overlayClassName?: string;
                shouldShowBorder?: boolean;
                shouldShowErrorBorder?: boolean;
                shouldShowUserAvatar: boolean;
                showLabel?: boolean;
                showOverlayShadow?: boolean;
                sizeModifier?: VideoWrapperSizeModifier;
                username: string;
            }
    >
> = ({
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    'wrapper-data-testid': wrapperDataTestId,
    children,
    className,
    isMirrored,
    isMobileDevice = false,
    isVideoInputMuted,
    labelContent,
    labelTestId,
    localMediaStream,
    overlayClassName,
    shouldShowBorder,
    shouldShowErrorBorder = false,
    shouldShowUserAvatar,
    showLabel,
    showOverlayShadow,
    sizeModifier,
    username,
    ...props
}) => {
    const {t} = useTranslation();
    const showVideo = isVideoActive(isVideoInputMuted, localMediaStream);
    const [isVideoInitiallyShown, setIsVideoInitiallyShown] = useState(false);

    useEffect(() => {
        if (showVideo) {
            setIsVideoInitiallyShown(true);
        }
    }, [showVideo]);

    return (
        <div className={styles.wrapper} {...props}>
            <VideoWrapper
                className={cx(
                    styles.videoWrapper,
                    {
                        [styles.withInfoBorder]: shouldShowBorder,
                        [styles.withErrorBorder]: shouldShowErrorBorder,
                    },
                    className,
                )}
                size={sizeModifier}
                background={'secondary'}
                alignItems="stretch"
                isFullHeight={true}
                data-testid={wrapperDataTestId ?? TestId.VideoSelfviewWrapper}
            >
                <Video
                    aria-label={ariaLabel ?? t('media.selfview', 'Selfview')}
                    data-testid={dataTestId ?? TestId.VideoSelfview}
                    isMirrored={isMirrored}
                    srcObject={localMediaStream}
                    disablePictureInPicture={isMobileDevice}
                    muted
                    className={cx(styles.video, {
                        [styles.hide]: !showVideo,
                    })}
                />
            </VideoWrapper>
            <SelfviewOverlay
                className={overlayClassName}
                isVideoInitiallyShown={isVideoInitiallyShown}
                labelContent={labelContent}
                labelTestId={labelTestId}
                showLabel={showLabel}
                showPlaceholder={
                    !showVideo && !shouldShowErrorBorder && shouldShowUserAvatar
                }
                showShadow={showOverlayShadow}
                username={username}
            >
                {children}
            </SelfviewOverlay>
        </div>
    );
};

export type SelfviewProps = React.ComponentProps<typeof Selfview>;
